import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { DelayArea } from "../models/DelayArea";
import { getDelayAreas } from "../repositories/DelayAreaRepository";

const millAssetArea: DelayArea = { AssetId: 10, AssetName: "Mill" };

type Props = {
	label?: string;
	value: number | null;
	onChange?: Function;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
	isFiltering?: boolean;
};

const useDelayAreas = (onError?: (error: Error) => void) => {
	return useQuery<DelayArea[], Error>({
		queryKey: ["delayAreas"],
		queryFn: ({ signal }) => getDelayAreas(signal),
		onError: onError,
	});
};

export const DelayAreaSelector = ({
	label = "Area",
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
	isFiltering = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: delayAreas, isLoading } = useDelayAreas((error) =>
		setError(error.message),
	);

	const filteredDelayAreas = isFiltering
		? [millAssetArea, ...(delayAreas ?? [])]
		: delayAreas;

	if (isLoading || !filteredDelayAreas) {
		return (
			<TextField
				label={label}
				disabled
				fullWidth
				InputProps={{
					endAdornment: <CircularProgress color="inherit" size={20} />,
				}}
			/>
		);
	}

	const selectedDelayArea =
		filteredDelayAreas?.find((da) => da.AssetId === value) ??
		filteredDelayAreas?.[0];

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				loading={isLoading}
				clearOnEscape
				disableClearable
				options={filteredDelayAreas || []}
				getOptionLabel={(option) => option.AssetName}
				isOptionEqualToValue={(option, value) =>
					option.AssetId === value.AssetId
				}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						sx={{
							backgroundColor: (theme) => theme.palette.background.paper,
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedDelayArea}
				onChange={(event, newValue) => {
					onChange(newValue?.AssetId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading assets"
			/>
		</>
	);
};
